import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { EmptystateBulletListColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import StackLockNotice from "components/StackLockNotice";
import PageWrapper from "components/PageWrapper";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateTask from "shared/Stack/access/useCanCreateTask";
import { AccountContext } from "views/AccountWrapper";

import StackDetachedIntegrationCallout from "../../components/DetachedIntegrationCallout";
import StackHeader from "../../components/Header";
import { StackContext } from "../../Context";
import { getStacksBackUrl } from "../../helpers";
import NewTaskForm from "../components/NewTaskForm";
import { STACK_TASKS_TABLE_CELLS } from "./Table/Cells";
import useStackTasksTableStore from "./Table/useTableStore";
import {
  STACK_TASKS_LIMIT,
  STACK_TASKS_LIMIT_STORAGE_KEY,
  STACK_TASKS_PAGINATION_OPTIONS,
} from "./constants";
import useStackTasksList from "./useListTasks";
import styles from "./styles.module.css";

const StackTasksList = () => {
  const { viewer } = useTypedContext(AccountContext);
  const { stack: ctxStack } = useTypedContext(StackContext);
  const canCreateTaskFn = useCanCreateTask();

  const {
    tasks,
    error,
    isPageLoading,
    loadingContent,
    onFetchItems,
    endCursor,
    startCursor,
    totalCount,
    paginationResetKey,
  } = useStackTasksList(ctxStack.id);

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: onFetchItems,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: tasks.length,
    initialLimit: STACK_TASKS_LIMIT,
    storageKey: STACK_TASKS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const hasRows = tasks.length > 0;

  useTitle(`Tasks · ${ctxStack.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: ctxStack.name,
    },
  ]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  const stack = ctxStack;
  const canCreateTask = canCreateTaskFn(stack);

  return (
    <>
      <StackHeader />

      <PageInfoHeader title="Tasks" />

      <StackDetachedIntegrationCallout />
      {stack.lockedBy !== null && (
        <StackLockNotice lockedAt={stack.lockedAt} lockedBy={stack.lockedBy} viewer={viewer.id} />
      )}
      <PageWrapper margin="0">
        {(canCreateTask.result || canCreateTask.message) && (
          <NewTaskForm accessErrorMessage={canCreateTask.message} stackId={stack.id} redesigned />
        )}
        {(hasRows || loadingContent) && (
          <>
            <TableSimple
              data={tasks}
              store={useStackTasksTableStore}
              ariaLabel="Stack tasks"
              cellPerColumn={STACK_TASKS_TABLE_CELLS}
              rowsPerPage={rowsPerPage}
              loading={loadingContent}
              wrapperClassName={styles.tableWrapper}
            />
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={STACK_TASKS_PAGINATION_OPTIONS}
            />
          </>
        )}
        {!hasRows && !loadingContent && (
          <EmptyState
            title="No tasks yet"
            icon={EmptystateBulletListColored}
            caption={
              <>
                Tasks are arbitrary one-off commands that you can run within the context of a fully
                configured and initialized Terraform workspace.{" "}
                {stack.canWrite && (
                  <>
                    Feel free to give it a try by running something innocent like{" "}
                    <code>ls -la</code>.
                  </>
                )}
              </>
            }
          />
        )}
      </PageWrapper>
    </>
  );
};

export default StackTasksList;
